import { useEffect, useRef } from 'react';

const inspectionSteps = [
  {
    step: '01',
    title: '예비조사',
    desc: '건축물 관련 도면, 설계 사양서, 자재 목록 등 문헌 자료를 검토하여 석면 함유 의심 자재를 사전 파악합니다.',
    icon: 'ri-file-search-line',
  },
  {
    step: '02',
    title: '기본조사',
    desc: '건축물 내·외부를 육안으로 조사하여 석면 함유 자재의 위치, 종류, 상태, 면적을 확인합니다.',
    icon: 'ri-eye-line',
  },
  {
    step: '03',
    title: '시료 채취',
    desc: '의심 자재에서 시료를 채취하여 공인 분석기관에 석면 함유 여부 및 종류를 분석 의뢰합니다.',
    icon: 'ri-test-tube-line',
  },
  {
    step: '04',
    title: '농도 측정',
    desc: '공기 중 석면 섬유 농도를 측정하여 작업 환경의 안전성을 평가하고 관련 기준 준수 여부를 확인합니다.',
    icon: 'ri-bar-chart-line',
  },
];

const measurementTypes = [
  {
    title: '공기 중 석면농도측정',
    items: ['석면 해체·제거 작업 전·중·후 측정', '작업 경계 및 인접 지역 농도 확인', '위상차현미경법(PCM) 분석', '산업안전보건법 및 석면안전관리법 기준 적합 여부 판정'],
    icon: 'ri-wind-line',
    bg: 'bg-green-950',
    textColor: 'text-white',
    subColor: 'text-green-300',
  },
  {
    title: '석면함유물질 분석',
    items: ['편광현미경법(PLM) 분석', '전자현미경(SEM/TEM) 분석', '1% 이상 석면 함유 여부 판정', '6종 석면 종류별 동정'],
    icon: 'ri-microscope-line',
    bg: 'bg-amber-500',
    textColor: 'text-green-950',
    subColor: 'text-green-900',
  },
];

export default function InspectionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="inspection" className="py-24 bg-stone-50" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-fade-in mb-16">
          <span className="inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
            Inspection &amp; Measurement
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-green-950 leading-tight whitespace-nowrap">
              석면조사 및 석면농도측정
            </h2>
            <p className="text-stone-500 text-base max-w-md leading-relaxed">
              정밀한 조사와 측정을 통해 석면 위험을 사전에 파악하고
              적절한 관리 방안을 수립합니다.
            </p>
          </div>
        </div>

        {/* PC: 이미지 + 스텝 2열, 모바일: 스텝만 전체 너비 */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* 현장조사 이미지 — PC만 표시 */}
          <div className="section-fade-in rounded-3xl overflow-hidden relative min-h-[400px] hidden lg:block">
            <img
              src="https://public.readdy.ai/ai/img_res/58781f9f4820603dabfc28c92b7cbee3.jpg"
              alt="석면 조사 현장"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-950/70 via-transparent to-transparent flex flex-col justify-end p-8">
              <div className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-300 text-xs font-semibold tracking-wide">현장 조사 진행 중</span>
                </div>
                <p className="text-white text-sm leading-relaxed">
                  건축물 해체·리모델링 전 반드시 석면조사를 실시해야 합니다.
                  석면조사기관에 의뢰하여 합법적으로 진행하세요.
                </p>
              </div>
            </div>
          </div>

          {/* 스텝 — 모바일에서 전체 너비 */}
          <div className="section-fade-in flex flex-col gap-4 lg:col-auto col-span-full">
            {inspectionSteps.map((step, i) => (
              <div
                key={step.step}
                className="section-fade-in flex items-start gap-5 bg-white rounded-2xl p-5 border border-stone-100 hover:border-green-200 hover:shadow-sm transition-all duration-200"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl border border-green-100">
                  <i className={`${step.icon} text-green-700 text-xl`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-bold text-amber-500 tracking-widest">STEP {step.step}</span>
                    <span className="font-bold text-green-950">{step.title}</span>
                  </div>
                  <p className="text-sm text-stone-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 측정 및 분석 서비스 — PC만 표시 */}
        <div className="hidden md:block">
          <div className="section-fade-in mb-8">
            <h3 className="text-2xl font-bold text-green-950 mb-2">측정 및 분석 서비스</h3>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {measurementTypes.map((type) => (
              <div key={type.title} className={`section-fade-in ${type.bg} rounded-3xl p-8`}>
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-5">
                  <i className={`${type.icon} ${type.textColor} text-2xl`}></i>
                </div>
                <h4 className={`text-xl font-bold ${type.textColor} mb-5`}>{type.title}</h4>
                <ul className="space-y-3">
                  {type.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <i className={`ri-check-line ${type.subColor} text-base mt-0.5 flex-shrink-0`}></i>
                      <span className={`text-sm ${type.textColor} opacity-90 leading-relaxed`}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
